import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Upload, FileText, ChevronDown, ChevronRight, Trash2, RefreshCw,
  Sparkles, BookOpen, Edit3, Eye, Plus, X, CheckCircle2, GripVertical,
} from 'lucide-react';

const MODE_LABELS = {
  blocking: { label: 'Bloquant', cls: 'bg-red-50 text-red-700 border-red-200' },
  non_blocking: { label: 'Non bloquant', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export const ManualsAdmin = () => {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState(null); // active manual for edit panel

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listManuals();
      setManuals(data);
      if (selected) {
        const fresh = data.find((m) => m.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      toast.error('Erreur de chargement des manuels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-8" data-testid="manuals-admin">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">MANUELS &amp; QCM</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
            Supports de cours &amp; <span className="font-serif italic text-[#C9B896]">contrôles d'apprentissage.</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Importe des manuels PDF, ajuste le sommaire dynamique et fais générer des QCM par l'IA pour vérifier
            l'acquis de chaque chapitre (présentiel ou distance).
          </p>
        </div>
        <Button
          onClick={() => setUploadOpen(true)}
          className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full"
          data-testid="manual-upload-btn"
        >
          <Upload className="w-4 h-4 mr-2" /> Importer un manuel
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-44 bg-white border border-[#1F2A4A]/8 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : manuals.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-[#1F2A4A]/8">
          <BookOpen className="w-12 h-12 text-[#C9B896] mx-auto mb-3 opacity-50" />
          <h3 className="text-lg font-medium text-foreground mb-1">Aucun manuel pour l'instant</h3>
          <p className="text-sm text-muted-foreground mb-5">Importe ton premier PDF pour démarrer.</p>
          <Button onClick={() => setUploadOpen(true)} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full">
            <Upload className="w-4 h-4 mr-2" /> Importer un manuel
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {manuals.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              className={`text-left bg-white border rounded-2xl p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                selected?.id === m.id ? 'border-[#C9B896] shadow-md' : 'border-[#1F2A4A]/10'
              }`}
              data-testid={`manual-card-${m.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1F2A4A]" />
                </div>
                {!m.is_published && (
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] uppercase tracking-wider">Brouillon</Badge>
                )}
              </div>
              <h3 className="font-medium text-[#1F2A4A] mb-1 line-clamp-1">{m.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{m.description || '—'}</p>
              <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[#C9B896]">
                <span>{m.page_count} pages</span>
                <span className="text-[#1F2A4A]/20">·</span>
                <span>{(m.chapters || []).length} chapitres</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <ManualDetailPanel
          manual={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}

      <UploadManualDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={() => { setUploadOpen(false); load(); }}
      />
    </div>
  );
};

// ─────────────────────────────────────────── Upload dialog
const UploadManualDialog = ({ open, onOpenChange, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setFile(null); setTitle(''); setDescription(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choisis un fichier PDF/DOCX');
    if (!title.trim()) return toast.error('Donne un titre au manuel');
    setSubmitting(true);
    try {
      await api.uploadManual(file, title.trim(), description.trim());
      toast.success('Manuel importé — le sommaire a été extrait automatiquement.');
      reset();
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="upload-manual-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Importer un manuel</DialogTitle>
          <DialogDescription>
            PDF, DOC, DOCX. Le sommaire sera extrait automatiquement (sur PDF avec marque-pages).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Manuel — Garnissage Auto" data-testid="manual-title-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Pour quelle formation, niveau, chapitres clés…" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Fichier</Label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-[#1F2A4A] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#C9B896]/15 file:text-[#1F2A4A] hover:file:bg-[#C9B896]/30"
              data-testid="manual-file-input"
              required
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} Mo
              </p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="manual-submit-upload">
              {submitting ? 'Import en cours…' : 'Importer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────── Detail panel (chapters + QCMs)
const ManualDetailPanel = ({ manual, onClose, onChanged }) => {
  const [chapters, setChapters] = useState(manual.chapters || []);
  const [savingChapters, setSavingChapters] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [qcms, setQcms] = useState([]);
  const [loadingQcms, setLoadingQcms] = useState(true);
  const [genChapterId, setGenChapterId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editingQcm, setEditingQcm] = useState(null);

  useEffect(() => { setChapters(manual.chapters || []); }, [manual]);

  const loadQcms = async () => {
    setLoadingQcms(true);
    try {
      const list = await api.listQcmsForManual(manual.id);
      setQcms(list);
    } catch (e) {
      toast.error('Erreur de chargement des QCM');
    } finally {
      setLoadingQcms(false);
    }
  };
  useEffect(() => { loadQcms(); /* eslint-disable-next-line */ }, [manual.id]);

  const moveChapter = (index, direction) => {
    const newArr = [...chapters];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newArr.length) return;
    [newArr[index], newArr[swapIdx]] = [newArr[swapIdx], newArr[index]];
    setChapters(newArr.map((c, i) => ({ ...c, order: i })));
  };

  const updateChapter = (idx, field, value) => {
    const next = [...chapters];
    next[idx] = { ...next[idx], [field]: value };
    setChapters(next);
  };

  const removeChapter = (idx) => {
    if (!window.confirm('Supprimer ce chapitre du sommaire ?')) return;
    setChapters(chapters.filter((_, i) => i !== idx).map((c, i) => ({ ...c, order: i })));
  };

  const addChapter = () => {
    setChapters([
      ...chapters,
      { id: crypto.randomUUID(), title: 'Nouveau chapitre', page_start: 1, page_end: 1, level: 1, order: chapters.length },
    ]);
  };

  const saveChapters = async () => {
    setSavingChapters(true);
    try {
      await api.updateManualChapters(manual.id, chapters);
      toast.success('Sommaire enregistré');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSavingChapters(false); }
  };

  const reExtract = async () => {
    if (!window.confirm('Réextraire le sommaire écrasera les chapitres actuels. Continuer ?')) return;
    setReextracting(true);
    try {
      const updated = await api.reExtractChapters(manual.id);
      setChapters(updated.chapters || []);
      toast.success('Sommaire réextrait');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setReextracting(false); }
  };

  const deleteManual = async () => {
    if (!window.confirm(`Supprimer définitivement « ${manual.title} » ? Les QCM associés seront aussi supprimés.`)) return;
    try {
      await api.deleteManual(manual.id);
      toast.success('Manuel supprimé');
      onChanged();
      onClose();
    } catch (e) { toast.error('Erreur'); }
  };

  const generateQcm = async (chapterId, mode) => {
    setGenChapterId(chapterId);
    setGenerating(true);
    try {
      const qcm = await api.generateQcmAI({
        manual_id: manual.id,
        chapter_id: chapterId,
        n_questions: 5,
        mode,
        passing_score: 70,
      });
      toast.success(`QCM généré avec ${qcm.questions.length} questions`);
      await loadQcms();
      onChanged();
      setEditingQcm(qcm);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de génération IA');
    } finally { setGenerating(false); setGenChapterId(null); }
  };

  const qcmByChapter = qcms.reduce((acc, q) => {
    if (q.chapter_id) acc[q.chapter_id] = q;
    return acc;
  }, {});

  return (
    <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl p-6 md:p-8 shadow-[0_30px_80px_-40px_rgba(31,42,74,0.25)]" data-testid={`manual-detail-${manual.id}`}>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">MANUEL SÉLECTIONNÉ</p>
          <h3 className="font-heading text-2xl text-[#1F2A4A]">{manual.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{manual.page_count} pages · {chapters.length} chapitres</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const axios = (await import('axios')).default;
                const resp = await axios.get(api.getManualFileUrl(manual.id), { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) { toast.error('Impossible de charger le PDF'); }
            }}
            className="rounded-full text-sm"
            data-testid="manual-open-pdf"
          >
            <Eye className="w-4 h-4 mr-2" /> Voir le PDF
          </Button>
          <Button variant="outline" onClick={reExtract} disabled={reextracting} className="rounded-full" data-testid="manual-reextract">
            <RefreshCw className={`w-4 h-4 mr-2 ${reextracting ? 'animate-spin' : ''}`} /> Réextraire
          </Button>
          <Button variant="ghost" onClick={deleteManual} className="text-red-600 hover:bg-red-50 rounded-full" data-testid="manual-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
          </Button>
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1px_360px] gap-6">
        {/* CHAPTERS LIST */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-[#1F2A4A]">Sommaire dynamique</h4>
            <Button size="sm" variant="ghost" onClick={addChapter} className="text-[#C9B896]">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </div>

          {chapters.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4">Aucun chapitre détecté. Ajoute-les manuellement.</p>
          ) : (
            <ul className="space-y-2" data-testid="chapters-list">
              {chapters.map((c, idx) => {
                const qcm = qcmByChapter[c.id];
                return (
                  <li key={c.id} className="bg-[#FAF7F0]/50 border border-[#1F2A4A]/8 rounded-xl p-3" data-testid={`chapter-row-${c.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <button onClick={() => moveChapter(idx, -1)} className="text-[#1F2A4A]/40 hover:text-[#1F2A4A]" title="Monter">▲</button>
                        <GripVertical className="w-3 h-3 text-[#1F2A4A]/30" />
                        <button onClick={() => moveChapter(idx, 1)} className="text-[#1F2A4A]/40 hover:text-[#1F2A4A]" title="Descendre">▼</button>
                      </div>
                      <div className="flex-1 grid md:grid-cols-[1fr_70px_70px_70px] gap-2">
                        <Input
                          value={c.title}
                          onChange={(e) => updateChapter(idx, 'title', e.target.value)}
                          className="h-9 text-sm"
                          placeholder="Titre"
                        />
                        <Input
                          type="number"
                          min="1"
                          value={c.page_start}
                          onChange={(e) => updateChapter(idx, 'page_start', parseInt(e.target.value) || 1)}
                          className="h-9 text-sm"
                          title="Page début"
                        />
                        <Input
                          type="number"
                          min="1"
                          value={c.page_end || ''}
                          onChange={(e) => updateChapter(idx, 'page_end', parseInt(e.target.value) || null)}
                          className="h-9 text-sm"
                          title="Page fin"
                          placeholder="fin"
                        />
                        <Select value={String(c.level)} onValueChange={(v) => updateChapter(idx, 'level', parseInt(v))}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Niveau 1</SelectItem>
                            <SelectItem value="2">Niveau 2</SelectItem>
                            <SelectItem value="3">Niveau 3</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <button onClick={() => removeChapter(idx)} className="text-red-500/70 hover:text-red-600 p-1" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* QCM zone for this chapter */}
                    <div className="mt-3 pl-9">
                      {qcm ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-[10px] border ${MODE_LABELS[qcm.mode].cls}`}>{MODE_LABELS[qcm.mode].label}</Badge>
                          <span className="text-xs text-[#1F2A4A]">
                            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />
                            QCM ({qcm.questions.length} questions · seuil {qcm.passing_score}%)
                          </span>
                          {qcm.ai_generated && (
                            <Badge className="bg-[#C9B896]/20 text-[#1F2A4A] border-[#C9B896]/40 text-[10px]">
                              <Sparkles className="w-3 h-3 inline mr-1" /> IA
                            </Badge>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setEditingQcm(qcm)} className="ml-auto text-xs h-7" data-testid={`qcm-edit-${qcm.id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Modifier
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            onClick={() => generateQcm(c.id, 'non_blocking')}
                            disabled={generating && genChapterId === c.id}
                            className="bg-[#C9B896]/20 hover:bg-[#C9B896]/40 text-[#1F2A4A] text-xs h-7"
                            data-testid={`generate-qcm-${c.id}`}
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            {generating && genChapterId === c.id ? 'Génération…' : 'Générer un QCM (IA)'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingQcm({
                              manual_id: manual.id, chapter_id: c.id,
                              title: `QCM — ${c.title}`, intro: '', mode: 'non_blocking',
                              passing_score: 70, questions: [], _new: true,
                            })}
                            className="text-xs h-7 text-[#1F2A4A]/70"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Créer à la main
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-5 flex justify-end">
            <Button onClick={saveChapters} disabled={savingChapters} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="save-chapters">
              {savingChapters ? 'Enregistrement…' : 'Enregistrer le sommaire'}
            </Button>
          </div>
        </div>

        <div className="hidden lg:block bg-[#1F2A4A]/8" />

        {/* Side: KPIs */}
        <aside className="space-y-3">
          <div className="bg-[#FAF7F0] rounded-2xl p-5 border border-[#1F2A4A]/8">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">QCM total</p>
            <p className="font-serif italic text-3xl text-[#1F2A4A]">{qcms.length}</p>
            <p className="text-xs text-muted-foreground mt-1">générés ou créés</p>
          </div>
          <div className="bg-[#FAF7F0] rounded-2xl p-5 border border-[#1F2A4A]/8">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">CHAPITRES SANS QCM</p>
            <p className="font-serif italic text-3xl text-[#1F2A4A]">
              {chapters.filter((c) => !qcmByChapter[c.id]).length}
            </p>
            <p className="text-xs text-muted-foreground mt-1">en attente</p>
          </div>
        </aside>
      </div>

      {/* QCM editor modal */}
      {editingQcm && (
        <QcmEditorDialog
          qcm={editingQcm}
          onClose={() => setEditingQcm(null)}
          onSaved={async () => { setEditingQcm(null); await loadQcms(); onChanged(); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────── QCM editor
const QcmEditorDialog = ({ qcm, onClose, onSaved }) => {
  const isNew = !!qcm._new;
  const [title, setTitle] = useState(qcm.title || '');
  const [intro, setIntro] = useState(qcm.intro || '');
  const [mode, setMode] = useState(qcm.mode || 'non_blocking');
  const [passingScore, setPassingScore] = useState(qcm.passing_score || 70);
  const [questions, setQuestions] = useState(qcm.questions || []);
  const [saving, setSaving] = useState(false);

  const addQuestion = () => {
    setQuestions([...questions, {
      id: crypto.randomUUID(),
      question: '', options: ['', '', '', ''],
      correct_index: 0, explanation: '',
    }]);
  };

  const updateQ = (idx, field, value) => {
    const next = [...questions];
    next[idx] = { ...next[idx], [field]: value };
    setQuestions(next);
  };

  const updateOption = (qIdx, optIdx, value) => {
    const next = [...questions];
    const opts = [...next[qIdx].options];
    opts[optIdx] = value;
    next[qIdx] = { ...next[qIdx], options: opts };
    setQuestions(next);
  };

  const removeQ = (idx) => setQuestions(questions.filter((_, i) => i !== idx));

  const save = async () => {
    if (!title.trim()) return toast.error('Titre obligatoire');
    if (questions.length === 0) return toast.error('Ajoute au moins une question');
    for (const q of questions) {
      if (!q.question.trim() || q.options.length < 2 || q.options.some((o) => !o.trim())) {
        return toast.error('Toutes les questions et options doivent être remplies');
      }
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(), intro: intro.trim(),
        mode, passing_score: parseInt(passingScore) || 70,
        questions,
      };
      if (isNew) {
        await api.createQcm({ ...payload, manual_id: qcm.manual_id, chapter_id: qcm.chapter_id });
        toast.success('QCM créé');
      } else {
        await api.updateQcm(qcm.id, payload);
        toast.success('QCM mis à jour');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (isNew) return onClose();
    if (!window.confirm('Supprimer ce QCM définitivement ?')) return;
    try {
      await api.deleteQcm(qcm.id);
      toast.success('QCM supprimé');
      onSaved();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="qcm-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isNew ? 'Nouveau QCM' : 'Éditer le QCM'}</DialogTitle>
          <DialogDescription>Affine les questions générées par l'IA ou crée-les manuellement.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Titre du QCM</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="qcm-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="qcm-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_blocking">Non bloquant</SelectItem>
                    <SelectItem value="blocking">Bloquant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Seuil (%)</Label>
                <Input type="number" min="0" max="100" value={passingScore} onChange={(e) => setPassingScore(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Introduction (facultatif)</Label>
            <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Questions ({questions.length})</Label>
              <Button size="sm" variant="ghost" onClick={addQuestion} className="text-[#C9B896]">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une question
              </Button>
            </div>
            <ul className="space-y-4">
              {questions.map((q, idx) => (
                <li key={q.id} className="bg-[#FAF7F0]/70 border border-[#1F2A4A]/8 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-[10px] text-[#C9B896] uppercase tracking-wider mt-2">Q{idx + 1}</span>
                    <div className="flex-1">
                      <Textarea
                        value={q.question}
                        onChange={(e) => updateQ(idx, 'question', e.target.value)}
                        placeholder="Énoncé de la question"
                        className="min-h-[60px]"
                      />
                    </div>
                    <button onClick={() => removeQ(idx)} className="text-red-500/70 hover:text-red-600 p-1 mt-1" title="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 pl-7">
                    {q.options.map((opt, optIdx) => (
                      <label key={optIdx} className="flex items-center gap-3">
                        <input
                          type="radio"
                          name={`q-${q.id}-correct`}
                          checked={q.correct_index === optIdx}
                          onChange={() => updateQ(idx, 'correct_index', optIdx)}
                          className="accent-[#C9B896]"
                        />
                        <Input
                          value={opt}
                          onChange={(e) => updateOption(idx, optIdx, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                          className="flex-1"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="pl-7">
                    <Input
                      value={q.explanation || ''}
                      onChange={(e) => updateQ(idx, 'explanation', e.target.value)}
                      placeholder="Explication (affichée après réponse)"
                      className="text-sm"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between pt-3 border-t">
            {!isNew && (
              <Button variant="ghost" onClick={remove} className="text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer le QCM
              </Button>
            )}
            <div className="flex gap-3 ml-auto">
              <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="qcm-save-btn">
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
